import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Form, Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import SmartPagination from "../Report/EmailCampaignRunReport/DynamicSmartPagination";
import ViewSettingModal from "./ViewSettingModal.js";
import {
  fetchDynamicGroupAgentList,
  bulkAddDynamicGroupAgents,
  updateDynamicGroupStatusDispatcher,
  manualDidAssignInitiateThunk
} from "../../store/slices/smartConnect/dynamicAgentGroup";
import { toast } from "react-toastify";
import { PERMISSIONS } from "../../contants/permission";
import { hasPermission } from "../../services/utils";
import { ICONS } from "../../constants/icons";
import {
  bulkAddSmartConnectAgents,
  updateAgent,
} from "../../store/slices/smartConnect/smartConnectAgent";
import AddNewAgentsComponent from "./AddNewAgents";

function DynamicAgentList({ groupId, groupStatus, selectedSetting }) {
  console.log("groupStatus : ", groupStatus);
  const dispatch = useDispatch();
  const [addNewAgentModal, setAddNewAgentModal] = useState(false);
  const [bulkOperationLoader, setBulkOperationLoader] = useState(false);
  const { agentList } = useSelector((store) => store.dynamicAgentGroupReducer);
  const {
    data = [],
    loading = false,
    error = null,
    pagination = {},
  } = agentList || {};

  const LIMIT = 10;
  const [page, setPage] = useState(1);
  const [filterAgentId, setFilterAgentId] = useState("");
  const [filterCqAgentId, setFilterCqAgentId] = useState("");
  const [updating, setUpdating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [smartConnectAgentId, setSmartConnectAgentId] = useState("");
  const [viewSettingOpen, setViewSettingOpen] = useState(false);
  const [isGroupActive, setIsGroupActive] = useState(groupStatus);

  // Bulk modal states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState({
    smart_connect_agent_id: undefined,
    cq_agent_id: undefined,
  });

  useEffect(() => {
    setIsGroupActive(groupStatus);
  }, [groupStatus]);

  useEffect(() => {
    if (groupId) {
      setPage(1);
      setAppliedFilters({
        smart_connect_agent_id: undefined,
        cq_agent_id: undefined,
      });
      setFilterAgentId("");
      setFilterCqAgentId("");
    }
  }, [groupId, addNewAgentModal]);

  const fetchData = useCallback(() => {
    if (!groupId) return;

    dispatch(
      fetchDynamicGroupAgentList({
        page,
        limit: LIMIT,
        filters: {
          group_id: groupId,
          ...appliedFilters,
        },
      }),
    );
  }, [dispatch, groupId, page, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleEditClick = (agent) => {
    setSelectedAgent(agent);
    setSmartConnectAgentId(agent.smart_connect_agent_id || "");
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedAgent(null);
    setSmartConnectAgentId("");
  };

  const handleUpdate = async () => {
    try {
      if (!smartConnectAgentId?.trim()) {
        toast.error("Smart Connect Agent ID is required");
        return;
      }

      if (!selectedAgent?.id || !groupId) {
        toast.error("Agent ID or Group ID is missing.");
        return;
      }

      const payload = {
        id: selectedAgent.id,
        group_id: groupId,
        cq_agent_id: selectedAgent.cq_agent_id,
        agent_email: selectedAgent?.agent_email,
        smart_connect_agent_id: smartConnectAgentId.trim(),
      };

      setUpdating(true);
      const msg = await dispatch(updateAgent(payload)).unwrap();
      toast.success(msg || "Agent updated successfully");
      fetchData();

      handleModalClose();
    } catch (err) {
      console.error("Error updating agent:", err);
      toast.error(
        typeof err === "string"
          ? err
          : err?.message || "Failed to update agent",
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters({
      smart_connect_agent_id: filterAgentId.trim() || undefined,
      cq_agent_id: filterCqAgentId.trim() || undefined,
    });
  };

  const handleReset = () => {
    setFilterAgentId("");
    setFilterCqAgentId("");
    setPage(1);
    setAppliedFilters({
      smart_connect_agent_id: undefined,
      cq_agent_id: undefined,
    });
  };

  // Bulk modal handlers
  const handleBulkClose = () => {
    setShowBulkModal(false);
    setCsvFile(null);
  };

  const handleGroupStatusToggle = async () => {
    const updatedStatus = !isGroupActive;

    const confirm = window.confirm(
      `Are you sure you want to ${updatedStatus ? "activate" : "deactivate"
      } this group?`,
    );

    if (!confirm) return;

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.data?.id;

      if (!userId) {
        toast.error("User not found. Please re-login.");
        return;
      }

      const payload = {
        groupId,
        groupStatus: updatedStatus,
        userId,
      };

      const msg = await dispatch(
        updateDynamicGroupStatusDispatcher(payload),
      ).unwrap();

      toast.success(msg || "Group status updated successfully");

      // Update UI ONLY after success
      setIsGroupActive(updatedStatus);
    } catch (err) {
      console.error("Error updating group status:", err);
      toast.error(
        typeof err === "string"
          ? err
          : err?.message || "Failed to update group status",
      );
    }
  };

  const handleBulkSubmit = async () => {
    setBulkOperationLoader(true);
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }

    if (!csvFile.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only CSV files are allowed");
      return;
    }

    if (!groupId) {
      toast.error("Group ID is missing");
      return;
    }

    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("group_id", groupId);

    try {
      const res = await dispatch(bulkAddSmartConnectAgents(formData)).unwrap();

      const msg =
        typeof res === "string"
          ? res
          : res?.message || "Bulk upload completed successfully";

      toast.success(msg);
      fetchData();
      handleBulkClose();
      setBulkOperationLoader(false);
    } catch (err) {
      console.error("Error in handleBulkSubmit:", err);
      const msg =
        typeof err === "string"
          ? err
          : err?.message ||
          "Bulk upload failed. Please check the CSV and try again.";

      toast.error(msg);
      setBulkOperationLoader(false);
    }
  };

  const handleDownloadSample = () => {
    const csvContent = ["CQ Agent ID,Smart Connect Agent ID"].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_dynamic_group_agent_ids.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleManualAssignDid = async (agent) => {
    try {
      if (!agent) {
        toast.error("Agent not found. Please Try again.");
        return;
      }

      if (!groupId) {
        toast.error("Agent Group Id is missing. Please Try Again.");
        return;
      }

      const payload = {
        agent, groupId
      }

      const msg = await dispatch(manualDidAssignInitiateThunk(payload)).unwrap();
      toast.success(msg);
    } catch (err) {
      const msg = typeof err === 'string' ? err : err.message;
      toast.error(msg || "Something went wrong.");
    }
  }

  return (
    <div>
      {/* Filters */}
      <div className="d-flex flex-wrap align-items-end gap-4 mt-3 mb-3 p-3 border rounded bg-light">
        {/* Filters */}
        <Form.Group>
          <Form.Label className="mb-1">Smart Connect Agent ID</Form.Label>
          <Form.Control
            value={filterAgentId}
            onChange={(e) => setFilterAgentId(e.target.value)}
            placeholder="Search Smart Connect Agent ID"
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="mb-1">CQ Agent ID</Form.Label>
          <Form.Control
            value={filterCqAgentId}
            onChange={(e) => setFilterCqAgentId(e.target.value)}
            placeholder="Search CQ Agent ID"
          />
        </Form.Group>

        <div className="d-flex gap-2">
          <Button onClick={handleSearch} disabled={loading}>
            Search
          </Button>
          <Button
            variant="outline-secondary"
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </Button>
        </div>

        {/* Right Action Panel */}
        <div className="ms-auto p-2 px-3 border rounded bg-white">
          {/* Status */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span
              className={`badge ${isGroupActive ? "bg-success" : "bg-secondary"
                }`}
            >
              {isGroupActive ? "Active" : "Inactive"}
            </span>

            <Form.Check
              type="switch"
              id="group-active-toggle"
              checked={isGroupActive}
              onChange={handleGroupStatusToggle}
            />
          </div>

          {/* Actions */}
          <div className="d-flex flex-column gap-2">
            {hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) && (
              <Button
                size="sm"
                onClick={() => setAddNewAgentModal(true)}
                disabled={!isGroupActive}
              >
                Add New Agents
              </Button>
            )}

            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setViewSettingOpen(true)}
            >
              View Settings
            </Button>
          </div>
        </div>
      </div>

      <ViewSettingModal
        show={viewSettingOpen}
        onHide={() => setViewSettingOpen(false)}
        setting={selectedSetting}
        onUpdated={fetchData}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Dynamic Agent List</h5>

        <div className="d-flex gap-2">
          {hasPermission(PERMISSIONS.SMART_CONNECT.CREATE) &&
            hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) && (
              <Button variant="primary" onClick={() => setShowBulkModal(true)}>
                Bulk Add Smart Connect Agent ID
              </Button>
            )}
        </div>
      </div>

      {/* Bulk Add Modal */}
      <Modal
        show={showBulkModal}
        onHide={handleBulkClose}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="py-3">
          <div className="d-flex justify-content-between align-items-center w-100">
            <Modal.Title className="fw-semibold fs-5">
              Bulk Add Smart Connect Agent IDs
            </Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="fw-medium m-0">Upload CSV File</Form.Label>

              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleDownloadSample}
              >
                Download Sample CSV
              </Button>
            </div>

            <Form.Control
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files[0])}
            />

            <Form.Text className="text-muted mt-2">
              CSV must contain{" "}
              <strong>CQ Agent ID, Smart Connect Agent ID </strong>
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end gap-2">
          <Button variant="outline-secondary" onClick={handleBulkClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleBulkSubmit}
            disabled={!csvFile}
          >
            {bulkOperationLoader ? "Loading..." : "Upload & Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Table */}
      <Table bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>SR.NO.</th>
            <th>AGENT NAME</th>
            <th>SMART CONNECT AGENT ID</th>
            <th>CREDITQ AGENT ID</th>
            <th>DEPARTMENT</th>
            <th>AGENT EMAIL</th>
            <th>STATUS</th>
            {hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) &&
              hasPermission(PERMISSIONS.SMART_CONNECT.CREATE) && (
                <th>ACTION</th>
              )}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={8} className="text-center">
                <Spinner size="sm" />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center text-muted">
                No agents found
              </td>
            </tr>
          ) : (
            data.map((agent, index) => (
              <tr key={agent.id}>
                <td>{(page - 1) * LIMIT + index + 1}</td>
                <td>{agent.agent_name || "NA"}</td>
                <td>{agent.smart_connect_agent_id || "NA"}</td>
                <td>{agent.cq_agent_id || "NA"}</td>
                <td>{agent.department || "NA"}</td>
                <td>{agent.agent_email || "NA"}</td>
                <td>{agent.status === 1 ? "Active" : "Inactive"}</td>
                {hasPermission(PERMISSIONS.SMART_CONNECT.CREATE) &&
                  hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) && (
                    <td>
                      <img
                        src={ICONS.edit_action}
                        alt="Edit"
                        height="22"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleEditClick(agent)}
                      />
                      &nbsp; &nbsp; &nbsp;
                      {
                        agent.smart_connect_agent_id && !agent?.agentHasDid && <Button onClick={() => handleManualAssignDid(agent)}>Assign DID</Button>
                      }
                    </td>
                  )}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {pagination?.totalPages > 1 && (
        <SmartPagination
          currentPage={page}
          totalPages={pagination.totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Edit Modal */}
      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Agent</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedAgent && (
            <>
              <Form.Group className="mb-2">
                <Form.Label>Agent Name</Form.Label>
                <Form.Control
                  value={selectedAgent.agent_name || "NA"}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>CreditQ Agent ID</Form.Label>
                <Form.Control
                  value={selectedAgent.cq_agent_id || "NA"}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Department</Form.Label>
                <Form.Control
                  value={selectedAgent.department || "NA"}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Status</Form.Label>
                <Form.Control
                  value={selectedAgent.status === 1 ? "Active" : "Inactive"}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Smart Connect Agent ID</Form.Label>
                <Form.Control
                  value={smartConnectAgentId}
                  onChange={(e) => setSmartConnectAgentId(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdate} disabled={updating}>
            {updating ? "Updating..." : "Update"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add New agents modal */}
      <AddNewAgentsComponent
        show={addNewAgentModal}
        onClose={() => setAddNewAgentModal(false)}
        groupId={groupId}
      />
    </div>
  );
}

export default DynamicAgentList;
